/**
 * Shared search parameter schema for all ramps routes
 * Ensures consistent URL state preservation across the entire flow
 */

import type { ServiceOptionCode } from "@/api/types/moneygram";
import { extractClientNameFromJwt } from "@/utils/jwt-decoder";

export interface RampsSearchSchema {
	// External partner params (from partner URL)
	// For v3 (JWT): token is passed, but operation/lang/asset_code/wallet_provider come from prepare endpoint
	// For v5 (OAuth): all params come from URL
	token?: string; // JWT token for v3 API (contains wallet address)
	accesstoken?: string; // OAuth token for v5 API
	transaction_id: string;
	wallet_provider: string; // v5 only - for v3, comes from prepare endpoint
	asset_code: string; // v5 only - for v3, comes from prepare endpoint
	operation: string; // v5 only - for v3, comes from prepare endpoint
	lang: string; // v5 only - for v3, comes from prepare endpoint
	walletAddress?: string; // Wallet address for V5 API (required when using accesstoken)
	refNumber?: string; // Reference number - present in v2 standalone status pages

	// Internal flow params (also from URL for bookmarking/sharing)
	amount: string;
	country: string; // DEPRECATED: Legacy field for backward compatibility
	payout_country?: string; // Where user wants to receive money
	country_src?: "manual" | "url" | "profile" | "service"; // Source of payout_country
	locks?: string; // Comma-separated list of locked fields
	state: string;
	step: string;
	from_review?: string; // Flag indicating navigation from review page
	issueCountry?: string; // ID issue country selection
	service_option?: ServiceOptionCode;
}

export function validateRampsSearch(
	search: Record<string, unknown>,
): RampsSearchSchema {
	return {
		// External partner params (from partner URL)
		token: search.token as string | undefined,
		accesstoken: search.accesstoken as string | undefined,
		transaction_id: (search.transaction_id as string) || "",
		wallet_provider: (search.wallet_provider as string) || "",
		asset_code: (search.asset_code as string) || "USDC",
		operation: (search.operation as string) || "withdraw",
		lang: (search.lang as string) || "en",
		walletAddress: search.walletAddress as string | undefined,
		refNumber: search.refNumber as string | undefined,

		// Internal flow params (also from URL for bookmarking/sharing)
		amount: (search.amount as string) || "",
		country: (search.country as string) || "", // DEPRECATED: Keep for backward compatibility, no default
		payout_country: search.payout_country as string | undefined,
		country_src: search.country_src as
			| "manual"
			| "url"
			| "profile"
			| "service"
			| undefined,
		locks: search.locks as string | undefined,
		state: (search.state as string) || "",
		step: (search.step as string) || "calculator",
		from_review: search.from_review as string | undefined,
		issueCountry: search.issueCountry as string | undefined,
		service_option: search.service_option as ServiceOptionCode | undefined,
	};
}

/**
 * Determines API version based on which token parameter is present in the URL
 * and whether context params are provided
 *
 * - accesstoken → v5 API (OAuth flow, all params from URL)
 * - token + context params (operation, wallet_provider, asset_code) → v2 API (JWT, context from URL)
 * - token + refNumber → v2 API (JWT, standalone status page pattern)
 * - token with client_name in JWT → v3 API (JWT with client_name field)
 * - token without context params or refNumber → v3 API (JWT, context from prepare endpoint)
 * - neither → defaults to v3 for backward compatibility
 */
export function getApiVersionFromSearchParams(
	search: RampsSearchSchema,
): "v2" | "v3" | "v5" {
	if (search.accesstoken) {
		return "v5";
	}

	if (search.token) {
		// Check for client_name in JWT - indicates v3 API
		const clientName = extractClientNameFromJwt(search.token);
		if (clientName) {
			return "v3"; // v3 style: JWT with client_name field
		}

		// JWT token - check for v2 indicators:
		// 1. Context params (operation, wallet_provider, asset_code) - full flow
		// 2. refNumber - standalone status page pattern
		const hasV2ContextParams = !!(
			search.operation &&
			search.wallet_provider &&
			search.asset_code
		);
		const hasRefNumber = !!search.refNumber;

		if (hasV2ContextParams || hasRefNumber) {
			return "v2"; // v2 style: JWT + context from URL or refNumber present
		}
		return "v3"; // v3 style: JWT + context from prepare endpoint
	}

	// Default to v3 for backward compatibility
	return "v3";
}

/**
 * Gets the auth token from search params (either token or accesstoken)
 */
export function getAuthTokenFromSearchParams(
	search: RampsSearchSchema,
): string {
	return search.accesstoken || search.token || "";
}
